import _ from "lodash";
import { Component } from "../components/components/component";
import { AuthProvider } from "./authProvider";

export interface RouterOptions {
    loading: () => Component;
    forbidden: () => Component;
    requireAuthentication: boolean;
    authProvider: AuthProvider;
    modules?: { [name: string]: any; };
}

export interface Route {
    path: string;
    name?: string;
    routerLink?: string;
    component: (() => Component) | string;
    children?: Route[];
    id?: number;
    loading?: () => Component;
    requireAuthentication?: boolean;
    requireRight?: string;
    alwaysReload?: boolean;

    /**
     * Specifies what the page's title should be for this route.
     */
    title?: string;
}

export interface RouteParams {
    parameters: { [key: string]: string };
    queryString: { [key: string]: string };
    back: boolean;
}

export interface RouterOutlet extends Component {
    activeComponent: Component;
    setRoute(route: Route): Promise<void>;
    setComponent(component: Component | null): void;
}

export interface RouterLink extends Component {
    setActive(): void;
    setInactive(): void;
    show(): void;
    hide(): void;
}

export class Router {

    activeParams: RouteParams;
    activeRoute: Route;
    private nextRouteId: number = 1;
    private options: RouterOptions;
    private kendoRouter: kendo.Router;
    private outlets: RouterOutlet[] = [];
    private activeRoutes: Route[] = [];
    private routes: Route[] = [];

    constructor(options: RouterOptions) {
        this.options = options;
        this.kendoRouter = new kendo.Router({
            pushState: true,
            change: e => {
                if (e.url == "#") {
                    e.preventDefault();
                    return;
                }

                // Prevent navigation when popups are open. In the
                // future, maybe we want the ability to handle this smarter,
                // but for now this prevents issues from arising.

                // Opacity check is because Save Changes popup could exist but
                // we already clicked Discard and it is in the process of closing.

                if (_.some($(".k-window"), x => (x as any).style.opacity != "0")) {
                    e.preventDefault();
                    return;
                }
            }
        });
    }

    start(): void {
        this.useRouterForInternalLinks();
        this.kendoRouter.start();
    }

    navigate(location: string): Promise<boolean> {
        return new Promise(resolve => {
            const lastOutlet = _.last(this.outlets);
            if (!lastOutlet.activeComponent) {
                resolve(true);
                this.kendoRouter.navigate(location);
                return;
            }
            lastOutlet.activeComponent.beforeNavigate().then(shouldNavigate => {
                if (shouldNavigate) {
                    resolve(true);
                    this.kendoRouter.navigate(location);
                } else {
                    this.updateRouterLinks();
                    resolve(false);
                }
            });
        });
    }

    replace(location: string, silent: boolean = false): void {
        this.kendoRouter.replace(location, silent);
    }

    back() {
        window.history.back();
    }

    forward() {
        window.history.forward();
    }

    go(delta: number) {
        window.history.go(delta);
    }

    registerOutlet(outlet: RouterOutlet) {
        this.outlets.push(outlet);
    }

    unregisterOutlet(outlet: RouterOutlet) {
        const index = this.outlets.indexOf(outlet);
        if (index != -1) {
            this.outlets = this.outlets.slice(0, index);
        }
    }

    loadModule(name: string): Promise<any> {
        return new Promise(resolve => this.options.modules[name](resolve));

        // Use for testing loading:
        // return new Promise(resolve => setTimeout(() => this.options.modules[name](resolve), 2000));
    }

    route(route: Route, parents?: Route[]): void {
        this.routes.push(route);

        parents = parents || [];

        route.id = this.nextRouteId;
        this.nextRouteId++;

        if (!route.loading) {
            if (parents.length > 0) {
                route.loading = parents[parents.length - 1].loading;
            } else {
                route.loading = this.options.loading;
            }
        }

        if (route.requireAuthentication === undefined) {
            if (parents.length > 0) {
                route.requireAuthentication = parents[parents.length - 1].requireAuthentication;
            } else {
                route.requireAuthentication = this.options.requireAuthentication;
            }
        }

        const routeList = _.clone(parents);
        routeList.push(route);

        let childHandlesThisRoute = false;
        if (route.children) {
            _.forEach(route.children, child => {
                if (!child.path) {
                    child.path = route.path;
                    childHandlesThisRoute = true;
                } else if (!child.path.startsWith("/")) {
                    child.path = (route.path ? (route.path + "/") : "") + child.path;
                }
                this.route(child, routeList);
            });
        }

        if (childHandlesThisRoute) {
            return;
        }

        // optional slash at the end
        route.path = route.path + "(/)";

        this.kendoRouter.route(route.path, async (...params: any[]) => {
            const parameterNames = getParameterNames(route.path);
            const routerArgs = getRouteParams(params, parameterNames);

            this.activeRoutes = routeList;
            this.activeRoute = routeList[routeList.length - 1];
            this.activeParams = routerArgs;

            let routeIndex = 0;
            let outletIndex = 0;

            this.updateRouterLinks();

            while (routeList.length > routeIndex && this.outlets.length > outletIndex) {
                const currentRoute = routeList[routeIndex];
                const currentOutlet = this.outlets[outletIndex];

                if (route.requireAuthentication && !this.options.authProvider.isUserAuthenticated()) {
                    this.options.authProvider.signin();
                    return;
                }

                if (route.requireRight && !this.options.authProvider.isUserAuthorized(route.requireRight)) {
                    this.outlets = this.outlets.slice(0, outletIndex + 1);
                    currentOutlet.setComponent(this.options.forbidden());
                    this.updateRouterLinks();
                    return;
                }

                await currentOutlet.setRoute(currentRoute);

                routeIndex += 1;
                outletIndex += 1;
            }

            if (outletIndex < this.outlets.length) {
                this.outlets = this.outlets.slice(0, outletIndex + 1);
                this.outlets[outletIndex].setComponent(null);
            }

            this.updateRouterLinks();
        });
    }

    private updateRouterLinks(): void {
        _.forEach(this.routes, route => {
            if (!route.routerLink) {
                return;
            }
            const elements = $(".router-link-" + route.routerLink);
            _.forEach(elements, element => {
                const component = $(element).data("me") as RouterLink;
                if (_.some(this.activeRoutes, x => x.id == route.id)) {
                    component.setActive();
                } else {
                    component.setInactive();
                }
                if (route.requireRight) {
                    if (this.options.authProvider.isUserAuthorized(route.requireRight)) {
                        component.show();
                    } else {
                        component.hide();
                    }
                }
            });
        });
    }

    private useRouterForInternalLinks(): void {
        var me = this;

        $("body").on("click", "a", function (e) {
            const href = $(this).attr("href");

            if (!href || href == "#") {
                return true;
            }

            const external =
                href.indexOf("http://") == 0 ||
                href.indexOf("https://") == 0 ||
                href.indexOf("www.") == 0 ||
                href.indexOf("mailto:") == 0;

            if (external) {
                return true;
            }

            me.navigate(href);
            return false;
        });
    }

}

function getParameterNames(path: string): string[] {
    return _.map(path.split(":"), name => {
        let index: number;

        index = name.indexOf("/");
        if (index != -1) {
            name = name.slice(0, index);
        }

        index = name.indexOf("(");
        if (index != -1) {
            name = name.slice(0, index);
        }

        index = name.indexOf(")");
        if (index != -1) {
            name = name.slice(0, index);
        }

        return name;
    }).slice(1);
}

function getRouteParams(allParameters: any[], parameterNames: string[]): RouteParams {
    const parameterValues: string[] = allParameters.slice(0, allParameters.length - 1);
    const queryString: { [key: string]: any } = allParameters[allParameters.length - 1];
    const back = !!queryString._back;

    delete queryString._back;

    const parameters: { [key: string]: string } = {};
    _.forEach(parameterValues, (parameterValue, index) => {
        const parameterName = parameterNames[index];
        parameters[parameterName] = decodeURIComponent(parameterValue);
    });

    return {
        parameters: parameters,
        queryString: queryString,
        back: back
    };
}
